import { Locale } from "i18n/locale";

// Structured content for the standalone legal documents (/terms-of-use,
// /travel-terms-of-use). Kept here as data (not i18n JSON) because the
// documents are long and deeply nested; a block array per language stays
// readable and keeps the markup out of the translation files.
//
// Inline emphasis: wrap a span in **double asterisks** to render it bold
// (see LegalDocument's inline parser). No other markup is supported.
export type LegalBlock =
  | { type: "h1"; text: string }
  | { type: "h2"; text: string }
  | { type: "h3"; text: string }
  | { type: "p"; text: string }
  | { type: "ul"; items: string[] };

const residenceTermsEN: LegalBlock[] = [
  { type: "h1", text: "Residences Terms of Use" },

  { type: "h2", text: "1. Introduction" },
  {
    type: "p",
    text: "By booking a property on our Online platform **Europea Residences SA**, you are accepting all the terms and conditions set forth in these local Terms of use of your reserved residence.",
  },
  {
    type: "p",
    text: "If you do not agree to each and all of these terms and conditions please do not use the Platform and our Services. We reserve the right, at our discretion, to change and or modify these Residences Terms at any time without notice and, unless otherwise indicated, such changes will become effective immediately upon posting; therefore, please check these Terms periodically for changes. We will display the effective date of these Terms at the top of this page.",
  },

  { type: "h2", text: "2. About Europea Residences" },
  {
    type: "p",
    text: "**Europea Residences SA** is a Belgian agency specialising in the online distribution & Management of holiday and serviced apart-hotel accommodations. We offer an online platform where users can browse, organise, and book short- and mid-term accommodations across various cities in Europe, including Paris and Brussels, for both leisure and professional mobility.",
  },
  {
    type: "p",
    text: "Europea Residences SA does not own any residence listed on our Platform.",
  },

  { type: "h2", text: "3. About the Residences" },
  {
    type: "p",
    text: "Each residence can be an Hotel Room, an Apart-Hotel, or an Apartment ( type Airbnb ) and provided in good faith as such.",
  },
  {
    type: "p",
    text: "Each unit is provided and controlled by owners or legal responsible and provided in good faith as such.",
  },

  { type: "h2", text: "4. Residences Rules" },
  {
    type: "p",
    text: "By making a reservation and staying at one of the published properties, you agree to comply with the following residence rules. These rules are in place to ensure a safe, peaceful, and enjoyable stay for all guests while respecting the property itself, the Owners belongings, and the peace of our neighbours.",
  },

  { type: "h3", text: "4.1 Check-In and Check-Out" },
  {
    type: "ul",
    items: [
      "**Check-in time** is usually between 3:00pm and 4:00pm, depending the size and location of the residence.",
      "**Check-out time** is 11:00 am.",
      "**Early check in :** Requests must be communicated in advance to our reservation team. We always do our best to allow early check-in when possible. Early check-ins are free of charge. Please note that the earliest time we can accommodate a check-in is 10:00 a.m.",
      "**Late check out :** Requests must be communicated to our reservation team the day before departure. We always do our best to allow a late check-out when possible. Late check-outs are free of charge. Please note that the latest time we can accommodate a late check-out is 6:00 p.m.",
      "**Check-in methods :** The check-in details will be communicated a few days prior to your arrival. If you have any specific requests related to check-in, please let us know in advance.",
    ],
  },

  { type: "h3", text: "4.2 Maximum Occupancy" },
  {
    type: "p",
    text: "The maximum number of guests allowed in the residence is determined by the Owners and stated on the online listing. Hosting more guests than the number specified in the listing is unauthorised. Any such requests must be approved in advance by the Owners of the property.",
  },

  { type: "h3", text: "4.3 No Smoking Policy" },
  {
    type: "ul",
    items: [
      "Smoking is strictly prohibited inside each property.",
      "Failure to comply will result in a fine of 500 € to cover cleaning and deodorization costs.",
      "An exception is allowed for properties with a garden or large terrace, but smoking is permitted strictly in outdoor areas only",
    ],
  },
  {
    type: "p",
    text: "We do not track your activities, or access any personal information others than those directly related to the use of our Apps",
  },

  { type: "h3", text: "4.4 No Parties or Events" },
  {
    type: "ul",
    items: [
      "Parties, events, or any loud gatherings are strictly prohibited.",
      "Violation of this policy may result in immediate termination of the stay without a refund and potential fines.",
      "Professional shootings or videos are strictly prohibited in any residence.",
    ],
  },

  { type: "h3", text: "4.5 Quiet Hours" },
  {
    type: "ul",
    items: [
      "Quiet hours are from 9:00pm to 7:00am for all the residences.",
      "Tenants are expected to be respectful of neighbours and maintain a peaceful environment at all times.",
    ],
  },

  { type: "h3", text: "4.6 Property Care and Maintenance" },
  {
    type: "ul",
    items: [
      "Tenants must take care of the property and its contents. Some residences are primary homes, so Tenants must respect the Owners' belongings and pay special attention to personal items, just as you would expect others to respect yours.",
      "Tenants must understand that some owners live in their property for most of the year, so they should be understanding of the presence of personal items in some residences. However, the owners always ensure there is space available for tenants to store their belongings during their stay.",
      "Any damage, spills, or maintenance issues must be reported immediately.",
      "Tenants are responsible for the cost of any damage caused during their stay.",
    ],
  },

  { type: "h3", text: "4.7 Garbage and Recycling" },
  {
    type: "ul",
    items: [
      "Garbage and recycling should be disposed of in designated bins.",
      "Tenants must follow local waste disposal guidelines.",
    ],
  },

  { type: "h3", text: "4.8 Pets" },
  {
    type: "ul",
    items: [
      "Pets are not allowed in most residences",
      "However, we love pets and we always strive to offer some pet-friendly residences. We have added filters online to help visitors easily find these specific properties. Feel free to send us a request, and we will check with the owners on your behalf.",
    ],
  },

  { type: "h3", text: "4.9 Security and Safety" },
  {
    type: "ul",
    items: [
      "Please ensure all doors and windows are locked when leaving the property.",
      "Please do not tamper with or disable any safety equipment.",
    ],
  },

  { type: "h3", text: "4.10 Energy Use" },
  {
    type: "p",
    text: "Tenants should use energy responsibly by switching off heating and lights when they are not at the residence.",
  },

  { type: "h3", text: "4.11 Compliance with Laws and Regulations" },
  {
    type: "p",
    text: "Tenants must comply with all applicable local laws and regulations during their stay.",
  },

  { type: "h3", text: "4.12 Right of cancelation in certain cases" },
  {
    type: "p",
    text: "Europea Residences and the Owners reserves the right to cancel any reservation if there is any suspicion of illegal activities or potential risk to the property or its neighbours. For instance if Tenants declines to show identification paper at check-in or before check-in.",
  },

  { type: "h2", text: "5. Limitation of Liability" },
  {
    type: "p",
    text: "Europea Residences strives to provide accurate listings and services with the information provided by the Owners / Responsible of the residences, but we do not guarantee that all information is error-free.",
  },
  { type: "p", text: "We are not responsible for:" },
  {
    type: "ul",
    items: [
      "Interruptions or issues related to transportation to or from the Destination.",
      "Unforeseen events that may affect your stay (e.g., force majeure, emergencies).",
      "Legal information regarding the status or classification of a residence (Hotel, Aparthotel, Apartment….) is provided by the property owners or partners. The owners' declarations are considered authoritative, and our agency is not able to verify third-party compliance with all applicable laws. We trust that owners and partners provide information in good faith, and we publish it as declared.",
      "Injuries that occurs during your stay or on your way to your stay accommodation.",
    ],
  },

  { type: "h2", text: "6. Governing Law and Dispute Resolution" },
  {
    type: "p",
    text: "These Residence Terms are governed by the laws of the property location, France if the residence is located in France, Belgium if the residence is located in Belgium, etc.",
  },

  { type: "h2", text: "7. Contact Us" },
  {
    type: "p",
    text: "For any questions regarding these Terms, please contact us via the contact form.",
  },
];

const residenceTermsFR: LegalBlock[] = [
  { type: "h1", text: "Conditions d’utilisation des résidences" },

  { type: "h2", text: "1. Introduction" },
  {
    type: "p",
    text: "En réservant un logement sur notre plateforme en ligne **Europea Residences SA**, vous acceptez l’ensemble des conditions énoncées dans les présentes conditions d’utilisation de la résidence que vous avez réservée.",
  },
  {
    type: "p",
    text: "Si vous n’acceptez pas l’intégralité de ces conditions, veuillez ne pas utiliser la Plateforme ni nos Services. Nous nous réservons le droit, à notre discrétion, de modifier ces Conditions des résidences à tout moment et sans préavis ; sauf indication contraire, ces modifications prennent effet immédiatement dès leur publication. Nous vous invitons donc à consulter régulièrement ces Conditions. La date d’entrée en vigueur de ces Conditions est indiquée en haut de cette page.",
  },

  { type: "h2", text: "2. À propos d’Europea Residences" },
  {
    type: "p",
    text: "**Europea Residences SA** est une agence belge spécialisée dans la distribution en ligne et la gestion de logements de vacances et d’apart-hôtels avec services. Nous proposons une plateforme en ligne où les utilisateurs peuvent parcourir, organiser et réserver des hébergements de courte et moyenne durée dans plusieurs villes d’Europe, notamment Paris et Bruxelles, aussi bien pour les loisirs que pour la mobilité professionnelle.",
  },
  {
    type: "p",
    text: "Europea Residences SA ne possède aucune des résidences répertoriées sur notre Plateforme.",
  },

  { type: "h2", text: "3. À propos des résidences" },
  {
    type: "p",
    text: "Chaque résidence peut être une chambre d’hôtel, un apart-hôtel ou un appartement (type Airbnb) et est fournie de bonne foi en tant que telle.",
  },
  {
    type: "p",
    text: "Chaque logement est fourni et contrôlé par les propriétaires ou les responsables légaux, et fourni de bonne foi en tant que tel.",
  },

  { type: "h2", text: "4. Règlement des résidences" },
  {
    type: "p",
    text: "En effectuant une réservation et en séjournant dans l’un des logements publiés, vous acceptez de respecter le règlement des résidences suivant. Ces règles visent à garantir un séjour sûr, paisible et agréable à tous les voyageurs, tout en respectant le logement lui-même, les biens des propriétaires et la tranquillité de nos voisins.",
  },

  { type: "h3", text: "4.1 Arrivée et départ" },
  {
    type: "ul",
    items: [
      "**Heure d’arrivée** : généralement entre 15h00 et 16h00, selon la taille et l’emplacement de la résidence.",
      "**Heure de départ** : 11h00.",
      "**Arrivée anticipée :** Les demandes doivent être communiquées à l’avance à notre équipe de réservation. Nous faisons toujours de notre mieux pour permettre une arrivée anticipée lorsque cela est possible. Les arrivées anticipées sont gratuites. Veuillez noter que l’heure la plus matinale à laquelle nous pouvons vous accueillir est 10h00.",
      "**Départ tardif :** Les demandes doivent être communiquées à notre équipe de réservation la veille du départ. Nous faisons toujours de notre mieux pour permettre un départ tardif lorsque cela est possible. Les départs tardifs sont gratuits. Veuillez noter que l’heure la plus tardive à laquelle nous pouvons accepter un départ est 18h00.",
      "**Modalités d’arrivée :** Les détails de l’arrivée vous seront communiqués quelques jours avant votre venue. Si vous avez des demandes particulières concernant votre arrivée, merci de nous en informer à l’avance.",
    ],
  },

  { type: "h3", text: "4.2 Capacité maximale" },
  {
    type: "p",
    text: "Le nombre maximum de voyageurs autorisés dans la résidence est déterminé par les propriétaires et indiqué sur l’annonce en ligne. Héberger plus de voyageurs que le nombre indiqué sur l’annonce n’est pas autorisé. Toute demande en ce sens doit être approuvée au préalable par les propriétaires du logement.",
  },

  { type: "h3", text: "4.3 Interdiction de fumer" },
  {
    type: "ul",
    items: [
      "Il est strictement interdit de fumer à l’intérieur de chaque logement.",
      "Tout manquement entraînera une amende de 500 € pour couvrir les frais de nettoyage et de désodorisation.",
      "Une exception est prévue pour les logements disposant d’un jardin ou d’une grande terrasse, mais il est permis de fumer uniquement dans les espaces extérieurs.",
    ],
  },
  {
    type: "p",
    text: "Nous ne suivons pas vos activités et n’accédons à aucune information personnelle autre que celles directement liées à l’utilisation de nos applications.",
  },

  { type: "h3", text: "4.4 Fêtes et événements interdits" },
  {
    type: "ul",
    items: [
      "Les fêtes, événements ou tout rassemblement bruyant sont strictement interdits.",
      "Toute violation de cette règle peut entraîner la fin immédiate du séjour, sans remboursement, ainsi que d’éventuelles amendes.",
      "Les prises de vue ou vidéos professionnelles sont strictement interdites dans toute résidence.",
    ],
  },

  { type: "h3", text: "4.5 Heures de calme" },
  {
    type: "ul",
    items: [
      "Les heures de calme sont de 21h00 à 7h00 pour toutes les résidences.",
      "Les locataires sont tenus de respecter le voisinage et de maintenir un environnement paisible à tout moment.",
    ],
  },

  { type: "h3", text: "4.6 Entretien et soin du logement" },
  {
    type: "ul",
    items: [
      "Les locataires doivent prendre soin du logement et de son contenu. Certaines résidences sont des habitations principales ; les locataires doivent donc respecter les biens des propriétaires et accorder une attention particulière aux effets personnels, comme vous attendriez des autres qu’ils respectent les vôtres.",
      "Les locataires doivent comprendre que certains propriétaires vivent dans leur logement une grande partie de l’année ; ils doivent donc faire preuve de compréhension quant à la présence d’effets personnels dans certaines résidences. Les propriétaires veillent toutefois toujours à laisser un espace disponible pour que les locataires puissent ranger leurs affaires pendant leur séjour.",
      "Tout dommage, déversement ou problème d’entretien doit être signalé immédiatement.",
      "Les locataires sont responsables du coût de tout dommage causé pendant leur séjour.",
    ],
  },

  { type: "h3", text: "4.7 Déchets et recyclage" },
  {
    type: "ul",
    items: [
      "Les déchets et le recyclage doivent être déposés dans les bacs prévus à cet effet.",
      "Les locataires doivent respecter les consignes locales de tri des déchets.",
    ],
  },

  { type: "h3", text: "4.8 Animaux de compagnie" },
  {
    type: "ul",
    items: [
      "Les animaux ne sont pas admis dans la plupart des résidences.",
      "Cependant, nous aimons les animaux et nous nous efforçons toujours de proposer des résidences qui les acceptent. Nous avons ajouté des filtres en ligne pour aider les visiteurs à trouver facilement ces logements spécifiques. N’hésitez pas à nous envoyer une demande, et nous vérifierons auprès des propriétaires pour vous.",
    ],
  },

  { type: "h3", text: "4.9 Sûreté et sécurité" },
  {
    type: "ul",
    items: [
      "Veuillez vous assurer que toutes les portes et fenêtres sont verrouillées lorsque vous quittez le logement.",
      "Veuillez ne pas manipuler ni désactiver les équipements de sécurité.",
    ],
  },

  { type: "h3", text: "4.10 Consommation d’énergie" },
  {
    type: "p",
    text: "Les locataires doivent utiliser l’énergie de manière responsable en éteignant le chauffage et les lumières lorsqu’ils ne sont pas dans la résidence.",
  },

  { type: "h3", text: "4.11 Respect des lois et réglementations" },
  {
    type: "p",
    text: "Les locataires doivent respecter l’ensemble des lois et réglementations locales applicables pendant leur séjour.",
  },

  { type: "h3", text: "4.12 Droit d’annulation dans certains cas" },
  {
    type: "p",
    text: "Europea Residences et les propriétaires se réservent le droit d’annuler toute réservation en cas de soupçon d’activités illégales ou de risque potentiel pour le logement ou son voisinage. Par exemple, si les locataires refusent de présenter une pièce d’identité à l’arrivée ou avant celle-ci.",
  },

  { type: "h2", text: "5. Limitation de responsabilité" },
  {
    type: "p",
    text: "Europea Residences s’efforce de fournir des annonces et des services exacts à partir des informations communiquées par les propriétaires / responsables des résidences, mais nous ne garantissons pas que toutes les informations soient exemptes d’erreurs.",
  },
  { type: "p", text: "Nous ne sommes pas responsables de :" },
  {
    type: "ul",
    items: [
      "Interruptions ou problèmes liés au transport vers ou depuis la destination.",
      "Événements imprévus susceptibles d’affecter votre séjour (par exemple, force majeure, urgences).",
      "Les informations légales relatives au statut ou à la classification d’une résidence (hôtel, apart-hôtel, appartement…) sont fournies par les propriétaires ou partenaires. Les déclarations des propriétaires font foi, et notre agence n’est pas en mesure de vérifier la conformité de tiers à l’ensemble des lois applicables. Nous faisons confiance aux propriétaires et partenaires pour fournir des informations de bonne foi, et nous les publions telles que déclarées.",
      "Les blessures survenant pendant votre séjour ou sur le trajet vers votre logement.",
    ],
  },

  { type: "h2", text: "6. Droit applicable et règlement des litiges" },
  {
    type: "p",
    text: "Les présentes Conditions des résidences sont régies par les lois du lieu où se situe le logement : la France si la résidence est située en France, la Belgique si la résidence est située en Belgique, etc.",
  },

  { type: "h2", text: "7. Nous contacter" },
  {
    type: "p",
    text: "Pour toute question concernant ces Conditions, veuillez nous contacter via le formulaire de contact.",
  },
];

export const getResidenceTerms = (locale: Locale): LegalBlock[] =>
  locale === "fr" ? residenceTermsFR : residenceTermsEN;

const travelTermsEN: LegalBlock[] = [
  { type: "h1", text: "Online Travel Services - Terms of Use" },

  { type: "h2", text: "1. Introduction" },
  {
    type: "p",
    text: "Welcome to Europea Residences! These Terms of Use (“Terms”) govern your access to and use of the website https://www.europea-residences.com (the “Site”), including any services provided through the Site (the “Services”), all mobile applications and operating systems designed and operated by Europea Residences.",
  },
  {
    type: "p",
    text: "The Terms of Service (“Terms”) are a legally binding contract between you and Europea Residences SA with its corporate headquarters located at 15 Monts des arts, 1000 Brussels.",
  },
  {
    type: "p",
    text: "By using the Platform, you are accepting all the terms and conditions set forth in these Terms. If you do not agree to each and all of these terms and conditions please do not use the Platform and our Services. We reserve the right, at our discretion, to change and or modify these Terms at any time without notice and, unless otherwise indicated, such changes will become effective immediately upon posting; therefore, please check these Terms periodically for changes. We will display the effective date of these Terms at the top of this page.",
  },

  { type: "h2", text: "2. About Europea Residences" },
  {
    type: "p",
    text: "**Europea Residences SA** is a Belgian agency specialising in the online distribution & Management of holiday and serviced apart-hotel accommodations. We offer an online platform where users can browse, organise, and book short- and mid-term accommodations across various cities in Europe, including Paris and Brussels, for both leisure and professional mobility. Europea Residences SA does not own any residence listed on our Platform.",
  },

  {
    type: "h2",
    text: "3. Privacy policy for the use of https://europea-residences.com",
  },

  { type: "h3", text: "3.1 Eligibility" },
  {
    type: "p",
    text: "To use the Site and reserve properties, you must be at least 18 years old and capable of entering into legally binding agreements.",
  },

  {
    type: "h3",
    text: "3.2 No account Registration / No app for users / No Cookies",
  },
  {
    type: "p",
    text: "**Europea Residences SA** does not require account creation on our online platform. You are free to make a reservation without the need to create an account. Once your reservation is confirmed, you will gain access to a secure online portal that is exclusively dedicated to managing your upcoming stay with us.",
  },
  {
    type: "p",
    text: "**Europea Residences SA** only lists properties that do not require the download of an app for check-in.",
  },
  {
    type: "p",
    text: "**Europea Residences SA** does not use cookies of any sort.",
  },
  {
    type: "p",
    text: "**Europea Residences SA** does not send any newsletters to any of our customers.",
  },

  { type: "h3", text: "3.3 Personal data" },
  {
    type: "p",
    text: "We are committed to keeping your personal data private and secure. We do not use, retarget, or market any personal data of our users.",
  },
  {
    type: "p",
    text: "While safeguarding your personal information is a priority, ensuring the security of the houses and residences listed on our platform is equally important. We kindly ask that you to provide at check-in identity information for yourself and any members of your travel party before accessing the properties published on our platform.",
  },

  { type: "h2", text: "4. Privacy policy for the use of Europea Mobile apps" },

  { type: "h3", text: "4.1 General" },
  {
    type: "p",
    text: "Appeuropea.com (“us”, “we”, or “our”) operates the different mobile applications of Europea Residences (the “Service”) used by our Owners / Partners / Team Members.",
  },
  {
    type: "p",
    text: "To improve and provide support, we use minor part of your data. By using the Service, you agree to the collection and use of information in accordance with this policy. Unless otherwise defined in this Privacy Policy, terms used in this Privacy Policy have the same meanings as in our Terms and Conditions.",
  },

  { type: "h3", text: "4.2 Limited Data Collected" },
  {
    type: "p",
    text: "While using our Service, we may ask you to provide us with certain personally identifiable information that can be used to contact or identify you (“Personal Data”). Personally identifiable information may include, but is not limited to:",
  },
  {
    type: "ul",
    items: [
      "Email address",
      "First name and last name",
      "Phone number",
      "Mobile device",
      "Type of Browser",
      "Unique device identifiers",
      "Diagnostic data in case of incident / crash",
    ],
  },
  { type: "p", text: "Examples of Cookies we use:" },
  {
    type: "ul",
    items: [
      "**Session Cookies.** We use Session Cookies to operate our Service.",
      "**Security Cookies.** We use Security Cookies for security purposes.",
    ],
  },

  { type: "h3", text: "4.3 Use of Data" },
  { type: "p", text: "appeuropea.com uses the collected data for various purposes:" },
  {
    type: "ul",
    items: [
      "To provide and maintain the Service",
      "To notify you about changes to our Service",
      "To provide customer care and support",
      "To provide analysis or valuable information so that we can improve the Service",
      "To monitor the usage of the Service",
      "To detect, prevent and address technical issues",
    ],
  },
  {
    type: "p",
    text: "We do not track your activities, or access any personal information others than those directly related to the use of our Apps",
  },

  { type: "h3", text: "4.4 Transfer Of Data" },
  {
    type: "p",
    text: "Your information, including technical Data, is transferred in 2 locations : Belgian Collection center, and India, Mumbai, Europea IT center. Our Apps use as well the technology of AWS Amazon for the cloud computing aspects.",
  },

  { type: "h3", text: "4.5 Disclosure Of Data" },
  {
    type: "p",
    text: "appeuropea.com may disclose your Personal Data to authorities in the good faith belief that such action is necessary to:",
  },
  {
    type: "ul",
    items: [
      "To comply with a legal obligation",
      "To protect and defend the rights or property of appeuropea.com",
      "To prevent or investigate possible wrongdoing in connection with the Service",
      "To protect the personal safety of users of the Service or the public",
      "To protect against legal liability",
    ],
  },

  { type: "h3", text: "4.6 Children’s Privacy" },
  {
    type: "p",
    text: "Our Service does not address anyone under the age of 18 (“Children”).",
  },

  { type: "h3", text: "4.7 Changes To This Privacy Policy" },
  {
    type: "p",
    text: "We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page.",
  },
  {
    type: "p",
    text: "We will let you know via email and/or a prominent notice on our Service, prior to the change becoming effective and update the “effective date” at the top of this Privacy Policy. You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.",
  },

  { type: "h2", text: "5. Online Reservation and Payment" },

  { type: "h3", text: "5.1 Reservation Process" },
  {
    type: "p",
    text: "We believe in automation and direct bookings. All properties listed on our platform offer online booking based on real-time calendar synchronisation. However, despite the use of IA technology to synchronise and block calendars, on rare occasions (approximately 3-4 times per year), calendar synchronisation may fail. In such cases, a full refund will be issued to the client if the upgrade proposal sent by our reservation team does not meet your needs",
  },

  { type: "h3", text: "5.2 Payment Terms" },
  {
    type: "ul",
    items: [
      "The payment methods accepted will be displayed during the booking process. You agree to use your credit card for payment, secured by 3D Secure technology. If the credit card used for the payment is not yours, we will require a photo of the credit card and a photo of the cardholder's ID after the reservation is made. Fraud attempts are common, so we kindly ask for your patience during this process.",
      "You will be able to choose also SEPA Wire transfer to our Collection Center located in Belgium during the payment process.",
      "Full payment may be required at the time of booking, except for stays longer than 3 months, where payment adjustments can be made.",
      "Rates includes Rent amount, tax and conciergerie fees. No extra will be required at your check-in.",
      "In order to keep a record of all financial flows, our company does not accept cash payments.",
    ],
  },

  { type: "h3", text: "5.3 Cancellations and Refunds" },
  {
    type: "p",
    text: "Refund eligibility depends on the specific cancellation terms applicable to your booking :",
  },
  {
    type: "ul",
    items: [
      "Free cancelation for a cancelation more than 2 months prior arrival or less than 24h after reservation ( Grace period ).",
      "50% cancelation fees for any cancelation between 2 months and 15 days prior arrival",
      "100% cancelation fees for any cancelation between 15 days and arrival date.",
    ],
  },
  {
    type: "p",
    text: "Any refund will be proceed by our accounting team at the latest 10 days after notice of cancelation received.",
  },

  { type: "h3", text: "5.4 Collection of City Tax" },
  {
    type: "p",
    text: "As an online agency, our company, through its collection center in Belgium, collects the city tax for each destination and transfers the correct amount to the respective municipalities.",
  },

  { type: "h2", text: "6. Limitation of Liability" },
  {
    type: "p",
    text: "Europea Residences strives to provide accurate listings and services with the information provided by the Owners / Responsible of the residences, but we do not guarantee that all information is error-free.",
  },
  { type: "p", text: "We are not responsible for:" },
  {
    type: "ul",
    items: [
      "Interruptions or issues related to website functionality.",
      "Unforeseen events that may affect your stay (e.g., force majeure, emergencies).",
      "Legal information regarding the status or classification of a residence (Hotel, Aparthotel, Apartment) is provided by the property owners or partners. The owners' declarations are considered authoritative, and our agency is not able to verify third-party compliance with applicable laws. We trust that owners and partners provide information in good faith, and we publish it as declared.",
      "General issues related to the neighbourhood or neighbours in the building.",
      "Issues related to works in the neighbourhood or in the common area.",
      "Injuries that occurs during your stay or on your way to your stay accommodation.",
    ],
  },

  { type: "h2", text: "7. Governing Law and Dispute Resolution" },
  {
    type: "p",
    text: "These Terms are governed by the laws of the Kingdom of Belgium.",
  },
];

const travelTermsFR: LegalBlock[] = [
  { type: "h1", text: "Services de voyage en ligne - Conditions d’utilisation" },

  { type: "h2", text: "1. Introduction" },
  {
    type: "p",
    text: "Bienvenue chez Europea Residences ! Les présentes conditions d’utilisation (« Conditions ») régissent votre accès au site web https://www.europea-residences.com (le « Site ») et son utilisation, y compris tout service fourni via le Site (les « Services »), ainsi que l’ensemble des applications mobiles et systèmes d’exploitation conçus et exploités par Europea Residences.",
  },
  {
    type: "p",
    text: "Les présentes conditions de service (« Conditions ») constituent un contrat juridiquement contraignant entre vous et Europea Residences SA, dont le siège social est situé au 15 Mont des Arts, 1000 Bruxelles.",
  },
  {
    type: "p",
    text: "En utilisant la Plateforme, vous acceptez l’ensemble des conditions énoncées dans les présentes Conditions. Si vous n’acceptez pas l’intégralité de ces conditions, veuillez ne pas utiliser la Plateforme ni nos Services. Nous nous réservons le droit, à notre discrétion, de modifier ces Conditions à tout moment et sans préavis ; sauf indication contraire, ces modifications prennent effet immédiatement dès leur publication. Nous vous invitons donc à consulter régulièrement ces Conditions. La date d’entrée en vigueur de ces Conditions est indiquée en haut de cette page.",
  },

  { type: "h2", text: "2. À propos d’Europea Residences" },
  {
    type: "p",
    text: "**Europea Residences SA** est une agence belge spécialisée dans la distribution en ligne et la gestion de logements de vacances et d’apart-hôtels avec services. Nous proposons une plateforme en ligne où les utilisateurs peuvent parcourir, organiser et réserver des hébergements de courte et moyenne durée dans plusieurs villes d’Europe, notamment Paris et Bruxelles, aussi bien pour les loisirs que pour la mobilité professionnelle. Europea Residences SA ne possède aucune des résidences répertoriées sur notre Plateforme.",
  },

  {
    type: "h2",
    text: "3. Politique de confidentialité relative à l’utilisation de https://europea-residences.com",
  },

  { type: "h3", text: "3.1 Éligibilité" },
  {
    type: "p",
    text: "Pour utiliser le Site et réserver des logements, vous devez être âgé d’au moins 18 ans et capable de conclure des accords juridiquement contraignants.",
  },

  {
    type: "h3",
    text: "3.2 Pas de création de compte / Pas d’application pour les utilisateurs / Pas de cookies",
  },
  {
    type: "p",
    text: "**Europea Residences SA** n’exige aucune création de compte sur notre plateforme en ligne. Vous êtes libre d’effectuer une réservation sans avoir à créer de compte. Une fois votre réservation confirmée, vous accéderez à un portail en ligne sécurisé exclusivement dédié à la gestion de votre prochain séjour chez nous.",
  },
  {
    type: "p",
    text: "**Europea Residences SA** ne publie que des logements ne nécessitant pas le téléchargement d’une application pour l’arrivée.",
  },
  {
    type: "p",
    text: "**Europea Residences SA** n’utilise aucun cookie, de quelque nature que ce soit.",
  },
  {
    type: "p",
    text: "**Europea Residences SA** n’envoie aucune newsletter à ses clients.",
  },

  { type: "h3", text: "3.3 Données personnelles" },
  {
    type: "p",
    text: "Nous nous engageons à préserver la confidentialité et la sécurité de vos données personnelles. Nous n’utilisons, ne reciblons ni ne commercialisons aucune donnée personnelle de nos utilisateurs.",
  },
  {
    type: "p",
    text: "Si la protection de vos informations personnelles est une priorité, la sécurité des maisons et résidences répertoriées sur notre plateforme l’est tout autant. Nous vous demandons de fournir, à l’arrivée, les informations d’identité vous concernant ainsi que celles de toute personne vous accompagnant, avant d’accéder aux logements publiés sur notre plateforme.",
  },

  {
    type: "h2",
    text: "4. Politique de confidentialité relative à l’utilisation des applications mobiles Europea",
  },

  { type: "h3", text: "4.1 Généralités" },
  {
    type: "p",
    text: "Appeuropea.com (« nous », « notre » ou « nos ») exploite les différentes applications mobiles d’Europea Residences (le « Service ») utilisées par nos propriétaires / partenaires / membres de l’équipe.",
  },
  {
    type: "p",
    text: "Afin d’améliorer et de fournir une assistance, nous utilisons une partie mineure de vos données. En utilisant le Service, vous acceptez la collecte et l’utilisation des informations conformément à la présente politique. Sauf définition contraire dans la présente politique de confidentialité, les termes qui y sont utilisés ont la même signification que dans nos Conditions générales.",
  },

  { type: "h3", text: "4.2 Données limitées collectées" },
  {
    type: "p",
    text: "Lors de l’utilisation de notre Service, nous pouvons vous demander de nous fournir certaines informations personnellement identifiables pouvant être utilisées pour vous contacter ou vous identifier (« Données personnelles »). Les informations personnellement identifiables peuvent inclure, sans s’y limiter :",
  },
  {
    type: "ul",
    items: [
      "Adresse e-mail",
      "Nom et prénom",
      "Numéro de téléphone",
      "Appareil mobile",
      "Type de navigateur",
      "Identifiants uniques d’appareil",
      "Données de diagnostic en cas d’incident / de plantage",
    ],
  },
  { type: "p", text: "Exemples de cookies que nous utilisons :" },
  {
    type: "ul",
    items: [
      "**Cookies de session.** Nous utilisons des cookies de session pour faire fonctionner notre Service.",
      "**Cookies de sécurité.** Nous utilisons des cookies de sécurité à des fins de sécurité.",
    ],
  },

  { type: "h3", text: "4.3 Utilisation des données" },
  {
    type: "p",
    text: "appeuropea.com utilise les données collectées à diverses fins :",
  },
  {
    type: "ul",
    items: [
      "Fournir et maintenir le Service",
      "Vous informer des modifications apportées à notre Service",
      "Assurer le service client et l’assistance",
      "Fournir des analyses ou des informations utiles afin d’améliorer le Service",
      "Surveiller l’utilisation du Service",
      "Détecter, prévenir et résoudre les problèmes techniques",
    ],
  },
  {
    type: "p",
    text: "Nous ne suivons pas vos activités et n’accédons à aucune information personnelle autre que celles directement liées à l’utilisation de nos applications.",
  },

  { type: "h3", text: "4.4 Transfert des données" },
  {
    type: "p",
    text: "Vos informations, y compris les données techniques, sont transférées vers deux emplacements : le centre de collecte belge et le centre informatique Europea situé à Mumbai, en Inde. Nos applications utilisent également la technologie AWS Amazon pour les aspects liés au cloud computing.",
  },

  { type: "h3", text: "4.5 Divulgation des données" },
  {
    type: "p",
    text: "appeuropea.com peut divulguer vos Données personnelles aux autorités, de bonne foi, lorsqu’une telle action est nécessaire pour :",
  },
  {
    type: "ul",
    items: [
      "Se conformer à une obligation légale",
      "Protéger et défendre les droits ou la propriété d’appeuropea.com",
      "Prévenir ou enquêter sur d’éventuels actes répréhensibles liés au Service",
      "Protéger la sécurité personnelle des utilisateurs du Service ou du public",
      "Se prémunir contre toute responsabilité juridique",
    ],
  },

  { type: "h3", text: "4.6 Confidentialité des enfants" },
  {
    type: "p",
    text: "Notre Service ne s’adresse à aucune personne âgée de moins de 18 ans (« Enfants »).",
  },

  { type: "h3", text: "4.7 Modifications de la présente politique de confidentialité" },
  {
    type: "p",
    text: "Nous pouvons mettre à jour notre politique de confidentialité de temps à autre. Nous vous informerons de toute modification en publiant la nouvelle politique de confidentialité sur cette page.",
  },
  {
    type: "p",
    text: "Nous vous en informerons par e-mail et/ou par un avis visible sur notre Service, avant que la modification ne prenne effet, et mettrons à jour la « date d’entrée en vigueur » en haut de la présente politique de confidentialité. Nous vous conseillons de consulter régulièrement cette politique de confidentialité pour prendre connaissance d’éventuelles modifications. Les modifications de la présente politique de confidentialité prennent effet dès leur publication sur cette page.",
  },

  { type: "h2", text: "5. Réservation et paiement en ligne" },

  { type: "h3", text: "5.1 Processus de réservation" },
  {
    type: "p",
    text: "Nous croyons en l’automatisation et aux réservations directes. Tous les logements publiés sur notre plateforme proposent une réservation en ligne basée sur une synchronisation des calendriers en temps réel. Cependant, malgré l’utilisation de la technologie d’IA pour synchroniser et bloquer les calendriers, dans de rares cas (environ 3 à 4 fois par an), la synchronisation des calendriers peut échouer. Dans ce cas, un remboursement intégral sera accordé au client si la proposition de surclassement envoyée par notre équipe de réservation ne répond pas à vos besoins.",
  },

  { type: "h3", text: "5.2 Conditions de paiement" },
  {
    type: "ul",
    items: [
      "Les modes de paiement acceptés seront affichés lors du processus de réservation. Vous acceptez d’utiliser votre carte de crédit pour le paiement, sécurisé par la technologie 3D Secure. Si la carte de crédit utilisée pour le paiement n’est pas la vôtre, nous exigerons une photo de la carte de crédit ainsi qu’une photo de la pièce d’identité du titulaire après la réservation. Les tentatives de fraude sont fréquentes ; nous vous remercions de votre patience durant ce processus.",
      "Vous pourrez également choisir le virement SEPA vers notre centre de collecte situé en Belgique lors du processus de paiement.",
      "Le paiement intégral peut être exigé au moment de la réservation, sauf pour les séjours de plus de 3 mois, pour lesquels des ajustements de paiement peuvent être effectués.",
      "Les tarifs comprennent le montant du loyer, les taxes et les frais de conciergerie. Aucun supplément ne vous sera demandé à votre arrivée.",
      "Afin de conserver une trace de tous les flux financiers, notre société n’accepte pas les paiements en espèces.",
    ],
  },

  { type: "h3", text: "5.3 Annulations et remboursements" },
  {
    type: "p",
    text: "L’admissibilité au remboursement dépend des conditions d’annulation spécifiques applicables à votre réservation :",
  },
  {
    type: "ul",
    items: [
      "Annulation gratuite pour toute annulation effectuée plus de 2 mois avant l’arrivée ou moins de 24 h après la réservation (période de grâce).",
      "50 % de frais d’annulation pour toute annulation effectuée entre 2 mois et 15 jours avant l’arrivée.",
      "100 % de frais d’annulation pour toute annulation effectuée entre 15 jours et la date d’arrivée.",
    ],
  },
  {
    type: "p",
    text: "Tout remboursement sera traité par notre service comptable au plus tard 10 jours après réception de l’avis d’annulation.",
  },

  { type: "h3", text: "5.4 Perception de la taxe de séjour" },
  {
    type: "p",
    text: "En tant qu’agence en ligne, notre société, par l’intermédiaire de son centre de collecte situé en Belgique, perçoit la taxe de séjour pour chaque destination et reverse le montant correct aux municipalités concernées.",
  },

  { type: "h2", text: "6. Limitation de responsabilité" },
  {
    type: "p",
    text: "Europea Residences s’efforce de fournir des annonces et des services exacts à partir des informations communiquées par les propriétaires / responsables des résidences, mais nous ne garantissons pas que toutes les informations soient exemptes d’erreurs.",
  },
  { type: "p", text: "Nous ne sommes pas responsables de :" },
  {
    type: "ul",
    items: [
      "Interruptions ou problèmes liés au fonctionnement du site web.",
      "Événements imprévus susceptibles d’affecter votre séjour (par exemple, force majeure, urgences).",
      "Les informations légales relatives au statut ou à la classification d’une résidence (hôtel, apart-hôtel, appartement) sont fournies par les propriétaires ou partenaires. Les déclarations des propriétaires font foi, et notre agence n’est pas en mesure de vérifier la conformité de tiers aux lois applicables. Nous faisons confiance aux propriétaires et partenaires pour fournir des informations de bonne foi, et nous les publions telles que déclarées.",
      "Les problèmes généraux liés au quartier ou aux voisins de l’immeuble.",
      "Les problèmes liés à des travaux dans le quartier ou dans les parties communes.",
      "Les blessures survenant pendant votre séjour ou sur le trajet vers votre logement.",
    ],
  },

  { type: "h2", text: "7. Droit applicable et règlement des litiges" },
  {
    type: "p",
    text: "Les présentes Conditions sont régies par les lois du Royaume de Belgique.",
  },
];

export const getTravelTerms = (locale: Locale): LegalBlock[] =>
  locale === "fr" ? travelTermsFR : travelTermsEN;
